import './assets/main.css'
// Icon webfont: menu entries arrive from the API as arbitrary `mdi-*` names,
// so the full font is loaded rather than a tree-shaken subset.
import '@mdi/font/css/materialdesignicons.min.css'

import { createPinia } from 'pinia'
import { createApp } from 'vue'

import App from './App.vue'
import { i18n } from './i18n'
import router from './router'
import { setErrorNotifier, setMaintenanceHandler, setSessionExpiredHandler } from './services/http'
import { useAuthStore } from './stores/auth'
import { useLocalesStore } from './stores/locales'
import { useMaintenanceStore } from './stores/maintenance'
import { useUiStore } from './stores/ui'
import { useUserStore } from './stores/user'
import { reloadForStaleChunk } from './utils/staleChunk'
import { hydrateTheme } from './utils/themePreference'

const app = createApp(App)

app.use(createPinia())
app.use(i18n)

// Wire the HTTP layer to the app *after* Pinia exists but *before* the router,
// so the very first navigation already reports errors properly.
const ui = useUiStore()
const auth = useAuthStore()
const user = useUserStore()
const maintenance = useMaintenanceStore()

// Every failed request becomes a red toast unless it opted out via meta.silent.
setErrorNotifier((error) => ui.notifyApiError(error))

// The refresh flow gave up: drop the session and send the visitor to the login
// page, keeping where they were so they can come back after signing in.
setSessionExpiredHandler(async () => {
  auth.clearSession()
  user.clear()
  const current = router.currentRoute.value
  if (current.name !== 'login') {
    await router.push({ name: 'login', query: { redirect: current.fullPath } })
  }
})

// An admin closed the site mid-session: adopt the state the 503 carried (or
// re-read it) and park the visitor on the notice. Staff stay where they are —
// the API keeps letting them through.
setMaintenanceHandler(async (state) => {
  if (state) {
    maintenance.state = state
    maintenance.loaded = true
  } else {
    await maintenance.fetch(true)
  }

  if (!maintenance.isActive || user.isStaff) return
  if (router.currentRoute.value.name !== 'maintenance') {
    await router.push({ name: 'maintenance' })
  }
})

// Le traduzioni vivono a database, ma la copia dell'ultima risposta valida sta in
// localStorage: applicarla **prima** del mount evita che chi ha già visitato il
// sito veda un istante di inglese. A freddo resta l'inglese del bundle, e lo store
// completa il lavoro durante il primo `bootstrapSession`.
useLocalesStore().hydrate()

// Normally already done by public/theme-boot.js before first paint; repeated
// here so the module's ref and the attribute can never disagree.
hydrateTheme()

// Vite's import helper reports every failed lazy chunk here, router views and
// async components alike; same cause and same cure as the router's onError.
// Deferred one tick: for a view, the router's handler runs first and reloads
// straight at the destination, and this plain reload is then skipped by the
// guard. It only takes over for chunks the router never sees.
window.addEventListener('vite:preloadError', () => {
  setTimeout(() => reloadForStaleChunk())
})

app.use(router)

// Keep <html lang> aligned with the initial locale for a11y and SEO.
document.documentElement.setAttribute('lang', i18n.global.locale.value)

app.mount('#app')
